import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import ManagementHeader from './ManagementHeader';
import MgField from './MgField';
import { createEquipment, fetchFactoriesForManagement, fetchFactoryLines } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import './management.css';

function EquipmentRegisterPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [factories, setFactories] = useState([]);
    const [lines, setLines] = useState([]);

    const [formData, setFormData] = useState({
        equipmentCode: '',
        equipmentName: '',
        model: '',
        factoryId: '',
        lineId: '',
        serialNumber: '',
        manufacturer: '',
        equipmentType: 'INJECTION_MOLDING',
        installDate: '',
        warrantyEndDate: '',
        status: 'IDLE',
        maxTemperature: '',
        maxPressure: '',
        ratedPower: '',
        targetCycleTime: '',
        cavityCount: '',
        lastInspectionDate: '',
        nextMaintenanceDate: '',
        description: ''
    });

    // 공장 목록 로드
    useEffect(() => {
        const loadFactories = async () => {
            try {
                const data = await fetchFactoriesForManagement();
                setFactories(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('공장 목록 조회 실패:', err);
                setFactories([]);
            }
        };

        loadFactories();
    }, []);

    // 선택된 공장의 라인 목록 로드 (공장 변경 시 선택된 라인 초기화)
    useEffect(() => {
        if (!formData.factoryId) {
            setLines([]);
            return;
        }

        const loadLines = async () => {
            try {
                const data = await fetchFactoryLines(formData.factoryId);
                setLines(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('라인 목록 조회 실패:', err);
                setLines([]);
            }
        };

        loadLines();
    }, [formData.factoryId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            // 공장을 바꾸면 이전 공장의 라인 선택은 무효이므로 해제
            ...(name === 'factoryId' ? { lineId: '' } : {})
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // API 요청 데이터 구성
            const equipmentData = {
                factoryId: parseInt(formData.factoryId),
                lineId: formData.lineId ? parseInt(formData.lineId, 10) : null,
                equipmentCode: formData.equipmentCode,
                equipmentName: formData.equipmentName,
                model: formData.model,
                manufacturer: formData.manufacturer,
                serialNumber: formData.serialNumber,
                equipmentType: formData.equipmentType,
                installDate: formData.installDate || null,
                warrantyEndDate: formData.warrantyEndDate || null,
                maxTemperature: formData.maxTemperature ? parseFloat(formData.maxTemperature) : null,
                maxPressure: formData.maxPressure ? parseFloat(formData.maxPressure) : null,
                ratedPower: formData.ratedPower ? parseFloat(formData.ratedPower) : null,
                targetCycleTime: formData.targetCycleTime ? parseInt(formData.targetCycleTime) * 1000 : null, // 초를 밀리초로 변환
                cavityCount: formData.cavityCount ? parseInt(formData.cavityCount) : null,
                status: formData.status,
                productName: null,
                currentMoldId: null,
                specifications: null,
                lastInspectionDate: formData.lastInspectionDate || null,
                nextMaintenanceDate: formData.nextMaintenanceDate || null,
                description: formData.description || null
            };

            await createEquipment(equipmentData);

            alert(t('pages.management.equipmentRegister.alertSuccess'));
            navigate('/equipment-list');
        } catch (err) {
            console.error('설비 등록 실패:', err);

            // 에러 메시지 상세 처리
            if (err.message.includes('already exists')) {
                alert(t('pages.management.equipmentRegister.alertDuplicate'));
            } else {
                alert(t('pages.management.equipmentRegister.alertFail'));
            }
        }
    };

    const L = (key) => t(`pages.management.equipmentRegister.label.${key}`);
    const P = (key) => t(`pages.management.equipmentRegister.placeholder.${key}`);
    /* 텍스트·숫자·날짜 입력 한 칸. 날짜 칸은 placeholder 를 쓰지 않는다 */
    const input = (name, { type = 'text', required = false, ...rest } = {}) => (
        <input
            type={type}
            name={name}
            className="mg-input"
            placeholder={type === 'date' ? undefined : P(name)}
            value={formData[name]}
            onChange={handleInputChange}
            required={required}
            {...rest}
        />
    );
    const num = (name, extra = {}) => input(name, { type: 'number', min: '0', ...extra });

    return (
        <MainLayout>
            <div className="mg-page">
                <ManagementHeader crumbs={[{ label: t('sidebar.management.equipmentRegister') }]} />

                <form className="mg-form" onSubmit={handleSubmit}>
                    {/* 기본 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.equipmentRegister.section.basic')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('equipmentCode')} required>{input('equipmentCode', { required: true })}</MgField>
                            <MgField label={L('equipmentName')} required>{input('equipmentName', { required: true })}</MgField>
                            <MgField label={L('model')} required>{input('model', { required: true })}</MgField>
                            <MgField label={L('factory')} required>
                                <select name="factoryId" className="mg-select" value={formData.factoryId} onChange={handleInputChange} required>
                                    <option value="">{P('factorySelect')}</option>
                                    {factories.map(factory => (
                                        <option key={factory.id} value={factory.id}>{factory.factoryName}</option>
                                    ))}
                                </select>
                            </MgField>
                            <MgField label={L('line')}>
                                <select name="lineId" className="mg-select" value={formData.lineId} onChange={handleInputChange} disabled={!formData.factoryId}>
                                    <option value="">{P('lineSelect')}</option>
                                    {lines.map(line => (
                                        <option key={line.id} value={line.id}>{line.lineName}</option>
                                    ))}
                                </select>
                            </MgField>
                            <MgField label={L('manufacturer')} required>{input('manufacturer', { required: true })}</MgField>
                            <MgField label={L('serialNumber')} required>{input('serialNumber', { required: true })}</MgField>
                            <MgField label={L('installDate')}>{input('installDate', { type: 'date' })}</MgField>
                            <MgField label={L('warrantyEndDate')}>{input('warrantyEndDate', { type: 'date' })}</MgField>
                            <MgField label={L('initialStatus')} required>
                                <select name="status" className="mg-select" value={formData.status} onChange={handleInputChange} required>
                                    <option value="RUNNING">{t('pages.management.equipmentRegister.statusOption.running')}</option>
                                    <option value="IDLE">{t('pages.management.equipmentRegister.statusOption.idle')}</option>
                                    <option value="STOPPED">{t('pages.management.equipmentRegister.statusOption.stopped')}</option>
                                    <option value="MAINTENANCE">{t('pages.management.equipmentRegister.statusOption.maintenance')}</option>
                                    <option value="FAULT">{t('pages.management.equipmentRegister.statusOption.fault')}</option>
                                    <option value="OFFLINE">{t('pages.management.equipmentRegister.statusOption.offline')}</option>
                                </select>
                            </MgField>
                        </div>
                    </div>

                    {/* 운영 사양 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.equipmentRegister.section.spec')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('maxTemperature')}>{num('maxTemperature', { step: '0.01' })}</MgField>
                            <MgField label={L('maxPressure')}>{num('maxPressure', { step: '0.01' })}</MgField>
                            <MgField label={L('ratedPower')}>{num('ratedPower', { step: '0.01' })}</MgField>
                            <MgField label={L('targetCycleTime')}>{num('targetCycleTime')}</MgField>
                            <MgField label={L('cavityCount')}>{num('cavityCount')}</MgField>
                            <MgField label={L('lastInspectionDate')}>{input('lastInspectionDate', { type: 'date' })}</MgField>
                            <MgField label={L('nextMaintenanceDate')}>{input('nextMaintenanceDate', { type: 'date' })}</MgField>
                            <MgField label={L('description')} full>
                                <textarea name="description" className="mg-textarea" placeholder={P('description')} value={formData.description} onChange={handleInputChange} rows="4" />
                            </MgField>
                        </div>
                    </div>

                    <div className="mg-form-actions">
                        <button type="button" className="mg-btn secondary" onClick={() => navigate('/equipment-list')}>
                            {t('pages.management.equipmentRegister.btn.cancel')}
                        </button>
                        <button type="submit" className="mg-btn primary">
                            {t('pages.management.equipmentRegister.btn.submit')}
                        </button>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
}

export default EquipmentRegisterPage;
